import { get, post, authFetch } from '../request';
import type { Session } from '@/types';

// ============ Projects ============

export interface ProjectData {
  id: string;
  name: string;
  icon?: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
  /** 项目状态：存在已发布试玩即 published，否则 draft */
  status?: 'published' | 'draft';
  _count?: { sessions: number };
  _summary?: {
    playableCount: number;
    generationBreakdown: Record<string, number>;
    latestPlayable?: { version: number; label: string | null; createdAt: string; previewUrl: string | null } | null;
    bestPlayable?: { version: number; score: number } | null;
    sessionCount: number;
  };
}

export function getProjects(): Promise<{ success: boolean; data: ProjectData[] }> {
  return get<{ success: boolean; data: ProjectData[] }>('/api/projects');
}

export function createProject(name: string, description?: string): Promise<{ success: boolean; data: ProjectData }> {
  return post<{ success: boolean; data: ProjectData }>('/api/projects', { name, description });
}

export function updateProject(id: string, data: { name?: string; icon?: string; description?: string }): Promise<void> {
  return authFetch(`/api/projects/${id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }).then(() => undefined);
}

/** 复制项目：后端生成全新 id 的新项目（名称自动追加「-副本」后缀），复制全部关联会话/试玩数据。 */
export function duplicateProject(id: string): Promise<{ success: boolean; data: ProjectData }> {
  return post<{ success: boolean; data: ProjectData }>(`/api/projects/${id}/duplicate`);
}

export function deleteProject(id: string): Promise<void> {
  return authFetch(`/api/projects/${id}`, { method: 'DELETE' }).then(() => undefined);
}

// ============ Sessions ============

/** 会话列表项（后端 /sessions 返回，含 title/projectId，区别于 SDK 的 Session 实体）。 */
export interface SessionListItem {
  id: string;
  title: string | null;
  projectId: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface SessionListResult {
  sessions: SessionListItem[];
  total: number;
  hasMore: boolean;
}

export function getSessions(projectId?: string): Promise<Session[]> {
  const qs = projectId ? `?projectId=${projectId}` : '';
  return get<{ success: boolean; data: { sessions: Session[] } }>(`/api/agent/sessions${qs}`)
    .then((res) => res.data.sessions);
}

/** 获取会话列表（含分页元数据），用于会话侧栏。 */
export function listSessions(params?: { projectId?: string; offset?: number; limit?: number }): Promise<SessionListResult> {
  const search = new URLSearchParams();
  if (params?.projectId) search.set('projectId', params.projectId);
  if (params?.offset != null) search.set('offset', String(params.offset));
  if (params?.limit != null) search.set('limit', String(params.limit));
  const qs = search.toString();
  return get<{ success: boolean; data: SessionListResult }>(`/api/agent/sessions${qs ? `?${qs}` : ''}`)
    .then((res) => res.data);
}

export function createSession(projectId?: string): Promise<{ success: boolean; data: Session }> {
  return post<{ success: boolean; data: Session }>('/api/agent/session', { projectId });
}

// DO-NOT-REVERT: BUG-20260807-001 删除必须校验 res.ok——此前不检查响应状态，后端 500 也当成功，
// 仅本地乐观移除，刷新后会话从 DB 复活。失败必须抛错让调用方提示用户。
export async function deleteSession(id: string): Promise<void> {
  const res = await authFetch(`/api/agent/session/${id}`, { method: 'DELETE' });
  if (!res.ok) {
    let message = `删除会话失败（HTTP ${res.status}）`;
    try {
      const body = await res.json();
      if (body?.message) message = body.message;
    } catch { /* 非 JSON 响应保持默认文案 */ }
    throw new Error(message);
  }
}

export function renameSession(id: string, title: string): Promise<void> {
  return authFetch(`/api/agent/session/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ title }),
  }).then(() => undefined);
}

/** 移动会话到项目（projectId 传 null 解除关联）。 */
export function moveSession(id: string, projectId: string | null): Promise<void> {
  return authFetch(`/api/agent/session/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ projectId }),
  }).then(() => undefined);
}

/** 保存会话级生图引擎；配置写入 metadata，刷新后由会话详情恢复。 */
export async function updateSessionImageProvider(id: string, imageProvider: 'jimeng' | 'wanxiang'): Promise<void> {
  const res = await authFetch(`/api/agent/session/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ imageProvider }),
  });
  if (!res.ok) throw new Error(`保存生图引擎失败（HTTP ${res.status}）`);
}

/** 保存会话当前选中的试玩版本，刷新/重新打开会话后恢复。 */
export async function updateSessionSelectedVersion(id: string, selectedVersionId: string): Promise<void> {
  const res = await authFetch(`/api/agent/session/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ selectedVersionId }),
  });
  if (!res.ok) throw new Error(`保存选中版本失败（HTTP ${res.status}）`);
}

export async function getSessionMessages(sessionId: string): Promise<import('@/types').Message[]> {
  const res = await get<{ success: boolean; data: { messages?: import('@/types').Message[] } }>(`/api/agent/session/${sessionId}`);
  return res.data.messages ?? [];
}

// ============ Message Card State (FR-10/11/12) ============

/** 标记最近一条 GDD 消息已确认（服务端持久化到消息 metadata，刷新后卡片仍锁定）。 */
export function markGddConfirmed(sessionId: string): Promise<void> {
  return authFetch(`/api/agent/session/${sessionId}/messages/mark-gdd-confirmed`, { method: 'POST' }).then(() => undefined);
}

/** 标记最近一条视频方案确认卡已确认。 */
export function markVideoConfirmed(sessionId: string): Promise<void> {
  return authFetch(`/api/agent/session/${sessionId}/messages/mark-video-confirmed`, { method: 'POST' }).then(() => undefined);
}

/** 标记最近一条视频互动方案确认卡已确认（FEAT-20260907-VIDEO-PLAN：服务端持久化，刷新后倒计时不再重启自动重发）。 */
export function markVideoPlanConfirmed(sessionId: string): Promise<void> {
  return authFetch(`/api/agent/session/${sessionId}/messages/mark-video-plan-confirmed`, { method: 'POST' }).then(() => undefined);
}

/** 标记最近一条 CHOICE 消息的选中值（服务端持久化到消息 metadata，刷新后仍保持选中锁定）。 */
export function markChoiceSelected(sessionId: string, field: string, value: string): Promise<void> {
  return authFetch(`/api/agent/session/${sessionId}/messages/mark-choice`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ field, value }),
  }).then(() => undefined);
}

// ============ Playable Publish ============

/** 手动发布试玩包到 OSS（生产环境） */
export interface PublishPlayableResult {
  success: boolean;
  previewUrl: string;
  downloadUrl: string;
  playableId: string;
}

export function publishPlayable(sessionId: string): Promise<PublishPlayableResult> {
  return post<PublishPlayableResult>(`/api/playable/${sessionId}/publish`);
}
